import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  StopCircle,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { relTime } from "@/lib/format";
import {
  autoExtractStatus,
  cancelAutoExtract,
  errMessage,
  startAutoExtractBulk,
} from "@/lib/api";

const POLL_MS = 3000;

const isLive = (data) =>
  data?.job?.status === "running" ||
  (data?.worker?.pending || 0) > 0 ||
  (data?.worker?.in_flight || []).length > 0;

/**
 * Approval mail -> queue automation.
 *
 * Shows what the background worker is doing and lets an admin push the whole
 * historical backlog of approved threads through extraction in one click.
 */
export const AutoExtractPanel = ({ isAdmin = false, onRowsAdded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const rowsRef = useRef(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const next = await autoExtractStatus();
      setData(next);
      const rows = next?.job?.rows_created || 0;
      if (rows > rowsRef.current && next?.job?.status === "running") onRowsAdded?.();
      rowsRef.current = rows;
      return next;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, [onRowsAdded]);

  // Poll only while something is actually in flight - idle pages stay quiet.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await refresh();
      if (cancelled) return;
      timerRef.current = setTimeout(tick, isLive(next) ? POLL_MS : POLL_MS * 6);
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refresh]);

  const start = async (retryFailed = false) => {
    setStarting(true);
    try {
      const res = await startAutoExtractBulk({ retryFailed });
      if (!res?.started) {
        toast(res?.reason || "Nothing to extract right now", {
          description: "Every approved mail already has rows in the queue.",
        });
      } else {
        toast.success(`Extracting ${res.job.total} approved mail${res.job.total === 1 ? "" : "s"}`, {
          description: "Rows appear in the queue as each mail is read. You can keep working.",
        });
      }
      await refresh();
    } catch (e) {
      toast.error("Could not start the extraction", { description: errMessage(e) });
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    try {
      await cancelAutoExtract(data?.job?.job_id);
      toast("Extraction stopped", { description: "Mails already being read will finish." });
      await refresh();
    } catch (e) {
      toast.error("Could not stop the run", { description: errMessage(e) });
    }
  };

  if (loading && !data) {
    return (
      <div
        className="vde-card flex items-center gap-2 p-3 text-xs text-muted-foreground"
        data-testid="auto-extract-panel-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Checking approval
        automation…
      </div>
    );
  }
  if (!data) return null;

  const job = data.job;
  const counts = data.counts || {};
  const worker = data.worker || {};
  const running = job?.status === "running";
  const live = isLive(data);
  const done = (job?.processed || 0) + (running ? 0 : 0);
  const pct = job?.total ? Math.min(100, Math.round((done / job.total) * 100)) : 0;
  const eligible = counts.eligible || 0;
  const failed = counts.failed || 0;
  const errors = job?.errors || [];

  return (
    <section className="vde-card p-3.5" data-testid="auto-extract-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            className={cn(
              "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
              live ? "bg-success-surface" : "bg-secondary"
            )}
            aria-hidden="true"
          >
            {live ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <Sparkles className="h-4 w-4 text-primary" />
            )}
          </span>
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold">Approval mail → queue automation</h2>
            <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
              {data.auto_extract_enabled
                ? "New mail that an approver signed off is extracted automatically after every sync."
                : "Automatic extraction is switched off in Settings - use the button to run it manually."}{" "}
              <span data-testid="auto-extract-eligible-count">
                {eligible
                  ? `${eligible} approved mail${eligible === 1 ? "" : "s"} still waiting.`
                  : "Everything approved is already in the queue."}
              </span>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAdmin ? (
            <>
              {running ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 bg-card"
                  onClick={stop}
                  data-testid="auto-extract-cancel-button"
                >
                  <StopCircle className="h-3.5 w-3.5" aria-hidden="true" /> Stop
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => start(false)}
                  disabled={starting || !eligible}
                  title={eligible ? undefined : "No approved mail is waiting"}
                  data-testid="auto-extract-start-button"
                >
                  {starting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Extract all approvals
                  {eligible ? (
                    <span className="vde-code ml-0.5 rounded-full bg-primary-foreground/20 px-1.5 text-[10px]">
                      {eligible}
                    </span>
                  ) : null}
                </Button>
              )}
              {!running && failed ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 bg-card"
                  onClick={() => start(true)}
                  disabled={starting}
                  data-testid="auto-extract-retry-button"
                >
                  <AlertTriangle className="h-3.5 w-3.5 text-danger-text" aria-hidden="true" /> Retry{" "}
                  {failed} failed
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {job && job.total ? (
        <div className="mt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-medium" data-testid="auto-extract-progress-label">
              {running ? "Reading approved mail" : "Last run"} · {done}/{job.total} done
              {job.status === "completed" ? " · finished" : ""}
              {job.status === "cancelled" ? " · stopped" : ""}
              {job.status === "interrupted" ? " · interrupted by a restart" : ""}
            </p>
            <p className="vde-code text-[10px] text-muted-foreground">
              {relTime(job.updated_at || job.created_at)}
            </p>
          </div>
          <Progress
            value={pct}
            className="mt-1.5 h-1.5 bg-secondary"
            aria-label="Extraction progress"
            data-testid="auto-extract-progress"
          />
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-full border border-success-border bg-success-surface px-2 py-0.5 text-[10px] font-semibold text-success-text"
              data-testid="auto-extract-rows-created"
            >
              <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" /> {job.rows_created || 0} row
              {(job.rows_created || 0) === 1 ? "" : "s"} queued
            </span>
            <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-medium">
              {job.succeeded || 0} mail{(job.succeeded || 0) === 1 ? "" : "s"} with vendors
            </span>
            {job.empty ? (
              <span className="rounded-full border border-warning-border bg-warning-surface px-2 py-0.5 text-[10px] font-medium text-warning-text">
                {job.empty} with nothing to read
              </span>
            ) : null}
            {job.skipped ? (
              <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {job.skipped} skipped
              </span>
            ) : null}
            {job.failed ? (
              <button
                type="button"
                onClick={() => setShowErrors((v) => !v)}
                className="inline-flex items-center gap-1 rounded-full border border-danger-border bg-danger-surface px-2 py-0.5 text-[10px] font-semibold text-danger-text transition-colors hover:bg-danger-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="auto-extract-failed-toggle"
              >
                <AlertTriangle className="h-2.5 w-2.5" aria-hidden="true" /> {job.failed} failed
                {showErrors ? (
                  <ChevronUp className="h-2.5 w-2.5" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-2.5 w-2.5" aria-hidden="true" />
                )}
              </button>
            ) : null}
            {worker.pending ? (
              <span className="vde-code text-[10px] text-muted-foreground">
                {worker.pending} waiting
              </span>
            ) : null}
          </div>

          {showErrors && errors.length ? (
            <ul className="mt-2 space-y-1" data-testid="auto-extract-error-list">
              {errors.slice(-6).map((e, i) => (
                <li
                  key={`${e.thread_id}-${i}`}
                  className="rounded-md border border-danger-border bg-danger-surface px-2 py-1.5 text-[10.5px] text-danger-text"
                >
                  <span className="font-semibold">{e.subject || e.thread_id}</span> — {e.error}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {(data.active_threads || []).length ? (
        <p
          className="mt-2 truncate text-[11px] text-muted-foreground"
          data-testid="auto-extract-current-thread"
        >
          Now reading: {data.active_threads.map((t) => t.subject || t.thread_id).join(" · ")}
        </p>
      ) : null}
    </section>
  );
};

export default AutoExtractPanel;
